import React, { useState, useEffect } from 'react';
import { Save, Store, Receipt, MapPin, Download } from 'lucide-react';

const API_URL = `/api/settings/profile`;

const Settings = () => {
  const [profile, setProfile] = useState({
    pump_name: '',
    dealer_code: '',
    gstin: '',
    address: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetch(API_URL, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          setProfile(data);
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch settings", err);
        setIsLoading(false);
      });
  }, []);

  const handleSave = () => {
    setIsSaving(true);
    fetch(API_URL, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(profile)
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          alert('Pump Profile saved successfully!');
        } else {
          alert('Failed to save profile: ' + data.error);
        }
      })
      .catch(err => alert('Network error while saving!'))
      .finally(() => setIsSaving(false));
  };

  if (isLoading) {
    return <div className="p-8 text-center text-slate-500 font-medium">Loading settings...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Manage your pump profile and system configurations.</p>
      </div>

      <div className="glass-panel overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-100 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <Store className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Pump Profile</h2>
              <p className="text-sm text-slate-500">This information will be printed on reports and WhatsApp summaries.</p>
            </div>
          </div>
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary flex items-center gap-2 px-6 py-2.5 shadow-sm"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Pump Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Store className="h-5 w-5 text-slate-400" />
                </div>
                <input 
                  type="text" 
                  className="input-field pl-10 bg-slate-50"
                  placeholder="e.g. Shree Balaji Fillings"
                  value={profile.pump_name}
                  onChange={e => setProfile({...profile, pump_name: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Dealer Code</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Receipt className="h-5 w-5 text-slate-400" />
                </div>
                <input 
                  type="text" 
                  className="input-field pl-10 bg-slate-50 font-mono"
                  placeholder="e.g. 1093452"
                  value={profile.dealer_code}
                  onChange={e => setProfile({...profile, dealer_code: e.target.value})}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">GSTIN Number</label>
            <div className="relative">
              <input 
                type="text" 
                className="input-field bg-slate-50 font-mono uppercase"
                placeholder="22AAAAA0000A1Z5"
                value={profile.gstin}
                onChange={e => setProfile({...profile, gstin: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Complete Address</label>
            <div className="relative">
              <div className="absolute top-3 left-3 pointer-events-none">
                <MapPin className="h-5 w-5 text-slate-400" />
              </div>
              <textarea 
                className="input-field pl-10 bg-slate-50 min-h-[100px] resize-y py-3"
                placeholder="123 Main Highway, City, State, ZIP"
                value={profile.address}
                onChange={e => setProfile({...profile, address: e.target.value})}
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      
      <div className="glass-panel overflow-hidden">
         <div className="bg-slate-50 border-b border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
               <Download className="w-5 h-5 text-indigo-500" /> Data Export (Coming Soon)
            </h2>
            <p className="text-sm text-slate-500 mt-1">Download historical data for accounting purposes.</p>
         </div>
         <div className="p-6">
            <button disabled className="px-4 py-2 bg-slate-100 text-slate-400 rounded-lg border border-slate-200 font-medium text-sm flex items-center gap-2 cursor-not-allowed">
               <Download className="w-4 h-4" /> Export All Meter Readings to CSV
            </button>
            <p className="text-xs text-slate-400 mt-2">This feature is scheduled for Phase 6.</p>
         </div>
      </div>
    </div>
  );
};

export default Settings;
